import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Media,
  MediaType,
  useCreateMutation,
  useCurrentWorkspace,
  useDeleteMutation,
  useFindByQuery,
  useUpdateMutation,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  Button,
  CategorySelector,
  Colors,
  ControlledTextInput,
  CustomAlert,
  SaveFormTop,
  Spacing,
  TopModalBar,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAudioPlayer } from 'expo-audio';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Toast from 'react-native-toast-message';
import * as yup from 'yup';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const audioSource = require('../../../static-assets/delete_pop.mp3');

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

type Category = {
  id: string;
  name: string;
};

type EditFileFormProps = {
  onComplete: () => void;
  fileId: string;
  /** When true, renders the Crop Circle layout (requires `media` prop). */
  useCropCircle?: boolean;
  /** Pre-loaded media — skips the internal fetch. Required when `useCropCircle` is true. */
  media?: Media;
  /** Called after the file is saved, before `onComplete`. Use to run additional mutations (e.g. update product). */
  onAfterSave?: () => Promise<void>;
  /** Overrides the default hard-delete. Use to remove the file from a parent entity instead. */
  onDelete?: () => Promise<void>;
  /** Called when the user taps the category add button; opens a third panel. */
  onCategoryEdit?: () => void;
  /** Controlled categories driven by a parent (e.g. third-panel picker). */
  externalCategories?: Category[];
  /** Notifies parent when categories change so it can keep its own state in sync. */
  onCategoriesChange?: (cats: Category[]) => void;
};

export const EditFileForm = ({
  onComplete,
  fileId,
  useCropCircle = false,
  media: mediaProp,
  onAfterSave,
  onDelete,
  onCategoryEdit,
  externalCategories,
  onCategoriesChange,
}: EditFileFormProps) => {
  const { t } = useTranslation();
  const currentWorkspace = useCurrentWorkspace();
  const slideLayout = useSlideLayoutSafe();

  const filter = useMemo(() => {
    return {
      id: { eq: fileId },
    };
  }, [fileId]);

  const { data, isLoading: isQueryLoading } = useFindByQuery({
    modelName: 'media',
    query: FIND_FILE,
    variables: { filter },
    options: { enabled: !mediaProp },
  });
  const isFileLoading = mediaProp ? false : isQueryLoading;

  const { categories } = useWorkspaceCategories();

  const { mutateAsync: createCategory, isPending: isCreatingCategory } =
    useCreateMutation({
      modelName: 'category',
      query: CREATE_CATEGORY,
    });

  const [fileName, setFileName] = useState('');
  const [fileCategories, setFileCategories] = useState<Category[]>([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const player = useAudioPlayer(audioSource);

  const { mutateAsync } = useUpdateMutation({
    modelName: 'media',
    query: UPDATE_FILE,
  });

  const { mutateAsync: deleteFileAsync } = useDeleteMutation({
    modelName: 'media',
    query: DELETE_FILE,
  });

  const formSchema = yup.object({
    name: yup.string().required('File name is required'),
    categories: yup
      .array()
      .of(
        yup.object({
          id: yup.string().required(),
          name: yup.string().required(),
        })
      )
      .optional(),
    newCategoryName: yup.string().optional(),
    linkUrl: yup.string().url(t('file.create.validation.link_invalid')).optional(),
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isDirty, isSubmitting },
  } = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: fileName,
      categories: fileCategories,
      newCategoryName: '',
      linkUrl: '',
    },
  });

  const selectedCategories = (watch('categories') || []) as Category[];
  const isLink = mediaProp?.type === MediaType.Link;
  const canSave = isDirty && !isSubmitting;

  useEffect(() => {
    if (!data || !data.items.length) return;

    const file = data.items[0];
    reset({
      name: file.name,
      categories: file.categories || [],
      newCategoryName: '',
      linkUrl: file.url ?? '',
    });
    setFileName(file.name);
    setFileCategories(file.categories || []);
  }, [data, reset]);

  // When media is pre-loaded (Crop Circle path), initialise form from it
  useEffect(() => {
    if (!mediaProp) return;
    reset({
      name: mediaProp.name ?? '',
      categories: (mediaProp.categories as Category[]) ?? [],
      newCategoryName: '',
      linkUrl: mediaProp.url ?? '',
    });
    setFileName(mediaProp.name ?? '');
    setFileCategories((mediaProp.categories as Category[]) ?? []);
  }, [mediaProp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync external controlled categories → form (driven by third-panel picker)
  useEffect(() => {
    if (!externalCategories) return;
    const current = (watch('categories') ?? []) as Category[];
    const formIds = current.map((c) => c.id).sort().join(',');
    const extIds = externalCategories.map((c) => c.id).sort().join(',');
    if (formIds !== extIds) {
      setValue('categories', externalCategories, { shouldDirty: true });
    }
  }, [externalCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateCategory = useCallback(async (name: string) => {
    try {
      const result = await createCategory({
        input: {
          name: name.trim(),
          workspace: { id: currentWorkspace.id },
        },
      });

      if (result?.id) {
        return {
          id: result.id,
          name: result.name,
        };
      }
      throw new Error('Failed to create category');
    } catch (error) {
      console.error('Failed to create category:', error);
      throw error;
    }
  }, [createCategory, currentWorkspace?.id]);

  const closeCategoryModal = () => {
    setIsModalOpen(false);
    // Clear the new category name field
    setValue('newCategoryName', '', { shouldDirty: false });
  };

  const onSubmit = async (data: yup.InferType<typeof formSchema>) => {
    const input: { id: string; name: string; categories: { id: string }[]; url?: string } = {
      id: fileId,
      name: data.name,
      categories: data.categories?.map((c) => ({ id: c.id })) || [],
    };
    if (isLink && data.linkUrl !== undefined) {
      input.url = data.linkUrl;
    }

    await mutateAsync({ input });
    await onAfterSave?.();
    onComplete();
  };

  const onFileDelete = async () => {
    if (onDelete) {
      await onDelete();
    } else {
      const input = { id: fileId };
      await deleteFileAsync({ input });
      showToast();
      player.play();
    }
    onComplete();
  };

  const handleCropCircleDeletePress = () => {
    const confirmDelete = async () => {
      await onFileDelete();
      slideLayout?.setColumnOverlay(null);
    };

    slideLayout?.setColumnOverlay(
      <View style={cropCircleOverlayStyles.overlay}>
        <View style={cropCircleOverlayStyles.sheet}>
          <Text style={cropCircleOverlayStyles.title}>
            {t('asset.detail.confirmDelete', { fileName })}
          </Text>
          <Text style={cropCircleOverlayStyles.subtitle}>
            {t('asset.detail.delete_warning')}
          </Text>
          <Button
            variant="rectangular"
            state="warning"
            style={cropCircleOverlayStyles.actionButton}
            onPress={confirmDelete}
          >
            {t('topNav.customAlert.yes')}
          </Button>
          <Button
            variant="rectangular"
            state="outline"
            style={cropCircleOverlayStyles.actionButton}
            onPress={() => slideLayout?.setColumnOverlay(null)}
          >
            {t('topNav.customAlert.cancel')}
          </Button>
        </View>
      </View>
    );
  };

  const showToast = () => {
    Toast.show({
      type: 'blackToast',
      text1: 'Woosh!',
      text2: `${fileName} has been deleted.`,
      position: 'bottom',
      autoHide: true,
      visibilityTime: 2500,
    });
  };

  return (
    !isFileLoading && (
      <>
        {useCropCircle ? (
          // ── Crop Circle layout ────────────────────────────────────────
          <>
            <ScrollView
              contentContainerStyle={cropCircleStyles.scrollContent}
              showsVerticalScrollIndicator={false}
            >
              <ControlledTextInput
                control={control}
                name="name"
                placeholder={t('file.create.title')}
                useCropCircle
                hideBorder
              />
              {isLink && (
                <ControlledTextInput
                  control={control}
                  name="linkUrl"
                  placeholder={t('file.create.linkUrl')}
                  useCropCircle
                  hideBorder
                />
              )}
              <CategorySelector
                selectedCategories={selectedCategories}
                availableCategories={categories ?? []}
                onCategoriesChange={(cats) => {
                  setValue('categories', cats, { shouldDirty: true });
                  onCategoriesChange?.(cats);
                }}
                onCreateCategory={handleCreateCategory}
                useCropCircle
                onSelectorEdit={onCategoryEdit}
              />
            </ScrollView>
            <View style={cropCircleStyles.footer}>
              <Button
                style={cropCircleStyles.saveButton}
                onPress={canSave ? handleSubmit(onSubmit) : undefined}
                state={canSave ? 'active' : 'default'}
                disabled={!canSave}
              >
                {t('asset.save')}
              </Button>
              <Button
                variant="rectangular"
                state="outline"
                style={cropCircleStyles.deleteButton}
                onPress={handleCropCircleDeletePress}
                size="large"
              >
                {t('asset.delete.button')}
              </Button>
            </View>
          </>
        ) : (
          // ── Legacy layout ─────────────────────────────────────────────
          <>
          <View style={{ flex: 1, width: '100%' }}>
          <SaveFormTop
            onSubmit={handleSubmit(onSubmit)}
            title={fileName}
            topBar={TopModalBar}
            isDirty={isDirty}
            onCancel={onComplete}
          >
            <View style={styles.innerFormContainer}>
              <ControlledTextInput
                control={control}
                name="name"
                placeholder="File Name dsfsdfsfds"
              />
              <View style={styles.categoriesContainer}>
                <CategorySelector
                  selectedCategories={selectedCategories}
                  availableCategories={categories}
                  onCategoriesChange={(newCategories) => {
                    setValue('categories', newCategories, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  onCreateCategory={handleCreateCategory}
                />
              </View>
            </View>
            <View style={styles.bottomContainer}>
              <Pressable
                style={styles.deleteButton}
                onPress={() => {
                  setShowDeleteAlert(true);
                }}
              >
                <Text style={styles.deleteButtonLabel}>
                  {t('file.deleteFile.deleteFile')}
                </Text>
              </Pressable>
            </View>
          </SaveFormTop>
        </View>

        {/* Create Category Modal */}
        {isModalOpen && (
          <View style={styles.modalOverlay}>
            <View style={styles.modalContainer}>
              <View style={styles.modalHeader}>
                <Text style={styles.modalTitle}>
                  Create Category
                </Text>
                <Pressable onPress={closeCategoryModal}>
                  <MaterialCommunityIcons
                    name="close"
                    size={24}
                    color={Colors.blackText}
                  />
                </Pressable>
              </View>
              <View style={styles.modalContent}>
                <ControlledTextInput
                  control={control}
                  name="newCategoryName"
                  placeholder="Enter category name"
                  key="new-category-input"
                />
                <View style={styles.modalButtons}>
                  <Pressable
                    style={styles.cancelButton}
                    onPress={closeCategoryModal}
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </Pressable>
                  <Pressable
                    style={styles.createButton}
                    onPress={() => {
                      const newName = watch('newCategoryName');
                      if (
                        newName &&
                        typeof newName === 'string' &&
                        newName.trim()
                      ) {
                        handleCreateCategory(newName);
                      }
                    }}
                    disabled={isCreatingCategory}
                  >
                    <Text style={styles.createButtonText}>
                      {isCreatingCategory ? 'Creating...' : 'Create'}
                    </Text>
                  </Pressable>
                </View>
              </View>
            </View>
          </View>
        )}

        <CustomAlert
          visible={showDeleteAlert}
          hideAlert={() => setShowDeleteAlert(false)}
          title={
            <>
              {t('file.deleteFile.alertBody1')} <b>{fileName}</b>?{' '}
            </>
          }
          primaryButtonLabel={t('file.deleteFile.delete')}
          secondaryButtonLabel={t('file.deleteFile.cancel')}
          action={() => onFileDelete()}
          containerStyle={styles.deleteAlertContainer}
        />
          </>
        )}
      </>
    )
  );
};

const styles = StyleSheet.create({
  innerFormContainer: {
    margin: 20,
  },
  categoriesContainer: {
    marginTop: 16,
  },
  bottomContainer: {
    flex: 1,
    borderTopWidth: 1,
    borderTopColor: Colors.graySpacer,
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 20,
    paddingVertical: 20,
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButtonLabel: {
    fontSize: 16,
    fontWeight: '600',
    letterSpacing: -0.25,
    color: Colors.danger,
  },
  deleteAlertContainer: {
    width: 350,
  },
  addNewButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 15,
    backgroundColor: Colors.primaryWithAlpha,
    borderRadius: 8,
    marginTop: 10,
  },
  buttonLabel: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: Colors.primaryDark,
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Colors.blackOverlay,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContainer: {
    backgroundColor: Colors.white,
    borderRadius: 10,
    width: '80%',
    maxWidth: 400,
    padding: 20,
    alignItems: 'center',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 15,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.blackText,
  },
  modalContent: {
    width: '100%',
    marginBottom: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    width: '100%',
  },
  cancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: Colors.secondaryGrayHeader,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.blackText,
  },
  createButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    backgroundColor: Colors.primary,
    borderRadius: 8,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.white,
  },
});

const cropCircleStyles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: 16,
    paddingBottom: 164,
    gap: 14,
  },
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: Spacing.baseMargin.base,
    paddingVertical: 20,
    backgroundColor: Colors.brand.white,
    flexDirection: 'column',
    alignItems: 'center',
  },
  saveButton: {
    marginBottom: Spacing.baseMargin.base,
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
  deleteButton: {
    marginBottom: 12,
    width: '100%',
    height: 48,
  },
});

const cropCircleOverlayStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: Colors.blackOverlay,
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: Colors.brand.white,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 24,
    alignItems: 'center',
  },
  title: {
    fontSize: 17,
    fontWeight: '600',
    color: Colors.black,
    marginBottom: 6,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 14,
    color: Colors.grayText,
    textAlign: 'center',
    marginBottom: 20,
  },
  actionButton: {
    marginBottom: 8,
    width: '100%',
  },
});

const UPDATE_FILE = `
  mutation UpdateFile($input: MutateMediaInput!) {
    updateMedia(input: $input) {
      id
    }
  }
`;

const DELETE_FILE = `
  mutation DeleteFile($input: MutateMediaInput!) {
    deleteMedia(input: $input) {
      id
    }
  }
`;

const FIND_FILE = `
  query FindFiles($filter: MediaFilter) {
    findMedia(filter: $filter) {
      items {
        id
        name
        url
        categories {
          id
          name
        }
      }
    }
  }
`;
